<template>
  <b-modal
    ref="delete-modal"
    size="md"
    hide-footer
    title="Xóa phiếu yêu cầu duyệt chi"
    id="delete-modal"
    @hidden="resetForm"
  >
    <div class="approval-delete-modal">
      <div class="alert alert-warning" role="alert" v-show="shouldShowWarning">
        Vui lòng xóa phiếu chi ở {{ getEntityNameByType() }}
        <strong
          @click="redirectByEntity"
          class="cursor-pointer text-decoration-underline"
          >{{ selectedItem.entityId }}</strong
        >
        trước khi xóa phiếu yêu cầu duyệt chi này.
      </div>

      <div class="form-group">
        <label for="deletedReason" class="font-weight-bold">
          Lý do xóa <span class="text-danger">*</span>
        </label>
        <b-form-textarea
          id="deletedReason"
          v-model.trim="form.deletedReason"
          placeholder="Nhập lý do xóa phiếu yêu cầu duyệt chi..."
          rows="4"
          max-rows="6"
          :state="!!validation.deletedReason.isValid"
          @input="validateReason"
        ></b-form-textarea>
        <b-form-invalid-feedback v-if="!validation.deletedReason.isValid">
          {{ validation.deletedReason.message }}
        </b-form-invalid-feedback>
      </div>

      <div class="d-flex justify-content-end mt-4">
        <b-button
          variant="secondary"
          size="sm"
          class="mr-2"
          @click="closeModal"
          :disabled="isSubmitting"
        >
          Hủy
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          @click="confirmDelete"
          :disabled="!isFormValid || isSubmitting"
        >
          <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
          <i
            class="flaticon2-rubbish-bin-delete-button mr-1"
            v-if="!isSubmitting"
          ></i>
          {{ isSubmitting ? 'Đang xóa...' : 'Xác nhận xóa' }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { makeToastFaile, makeToastSuccess } from '@/utils/common';
import { APPROVAL_ENTITY_TYPE } from '@/utils/enum';
import { APROVAL_ENTITY_NAME } from '@/utils/enum-name';

export default {
  name: 'ApprovalDeleteModal',
  data() {
    return {
      selectedItem: {},
      form: {
        deletedReason: '',
      },
      validation: {
        deletedReason: {
          isValid: null,
          message: '',
        },
        warning: {
          isValid: null,
          message: '',
        },
      },
      isSubmitting: false,
      isValidating: false,
    };
  },
  computed: {
    isFormValid() {
      return (
        this.form.deletedReason.length > 1 && !!this.validation.warning.isValid
      );
    },
    shouldShowWarning() {
      return !this.validation.warning.isValid && !this.isValidating;
    },
  },
  watch: {
    'selectedItem.entityId': {
      async handler() {
        if (!this.selectedItem.entityId) return;
        await this.canDeleteApproval();
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    showModal(item) {
      this.resetForm();
      this.selectedItem = { ...item };
      this.$refs['delete-modal'].show();
    },

    closeModal() {
      this.$refs['delete-modal'].hide();
    },

    resetForm() {
      this.form = {
        deletedReason: '',
      };
      this.validation = {
        deletedReason: {
          isValid: null,
          message: '',
        },
        warning: {
          isValid: null,
          message: '',
        },
      };
      this.isSubmitting = false;
      this.selectedItem = {};
    },

    async canDeleteApproval() {
      this.isValidating = true;

      try {
        const payload = {
          entityType: this.selectedItem.entityType,
          entityId: this.selectedItem.entityId,
          entityCode: this.selectedItem.entityCode,
        };
        const { data } = await ApiService.post('v2/approvals/can-delete', {
          data: payload,
        });
        this.validation.warning = {
          isValid: true,
          message: data.message,
        };
      } catch (error) {
        let message = '';
        if (!error.response) {
          makeToastFaile('Lỗi kết nối! Vui lòng thử lại.');
        } else {
          message =
            error.response.data?.message || 'Có lỗi xảy ra khi kểm tra phiếu';
        }
        this.validation.warning = {
          isValid: false,
          message: message,
        };
      } finally {
        this.isValidating = false;
      }
    },

    validateReason() {
      const reason = this.form.deletedReason;

      if (!reason) {
        this.validation.deletedReason = {
          isValid: false,
          message: 'Vui lòng nhập lí do xóa!',
        };
        return false;
      }

      if (reason.length > 1000) {
        this.validation.deletedReason = {
          isValid: false,
          message: 'Lý do xóa không được vượt quá 1000 ký tự',
        };
        return false;
      }

      this.validation.deletedReason = {
        isValid: true,
        message: '',
      };
      return true;
    },

    getEntityNameByType() {
      return APROVAL_ENTITY_NAME[this.selectedItem.entityType] || '';
    },

    redirectByEntity() {
      const entityType = this.selectedItem.entityType;
      if (entityType === APPROVAL_ENTITY_TYPE.ORDER) {
        this.$router.push({
          name: 'edit-order',
          params: {
            id: this.selectedItem.entityId,
          },
        });
      } else if (entityType === APPROVAL_ENTITY_TYPE.BILL) {
        this.$router.push({
          name: 'update-bill',
          query: {
            id: this.selectedItem.entityId,
          },
        });
      } else if (entityType === APPROVAL_ENTITY_TYPE.STOCK_SLIP) {
        this.$router.push({
          name: 'update-import-trade-in',
          query: {
            id: this.selectedItem.entityId,
          },
        });
      }
    },

    async confirmDelete() {
      if (!this.validateReason()) {
        return;
      }

      this.isSubmitting = true;

      try {
        const payload = {
          deletedReason: this.form.deletedReason,
        };

        const { data } = await ApiService.put(
          `v2/approvals/delete/${this.selectedItem.id}`,
          {
            data: payload,
          },
        );

        makeToastSuccess(
          data.message || 'Xóa phiếu yêu cầu duyệt chi thành công',
        );
        this.$emit('delete-success');
        this.closeModal();
      } catch (error) {
        if (!error.response) {
          makeToastFaile('Lỗi kết nối! Vui lòng thử lại.');
        } else {
          const message =
            error.response.data?.message || 'Có lỗi xảy ra khi xóa phiếu';
          makeToastFaile(message);
        }
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.approval-delete-modal {
  .alert {
    border-left: 4px solid #f0ad4e;
  }

  .form-group {
    margin-bottom: 1rem;

    label {
      margin-bottom: 0.5rem;
      font-size: 14px;
    }

    textarea {
      font-size: 14px;

      &:focus {
        border-color: #80bdff;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
      }
    }
  }

  .btn {
    min-width: 100px;
    font-weight: 600;
  }
}
</style>
